import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Grid,
  useTheme,
  Typography,
} from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/InfoContext";
import { SpinnerContext } from "../../contexts/SpinnerContext";
import useUserClaims from "../../hooks/useUserClaims";
import getRequiredUserData from "../../utils/getBranchInfo";
import DynamicTable from "../DynamicTable/DynamicTable";
import SearchInput from "../VersatileComponents/SearchInput";
import ConfirmationDialog from "../VersatileComponents/ConfirmationDialog";
import BillForm from "./BillForm";
import BillCreateForm from "./BillCreateForm";
import { getBills, searchBills, deleteBill } from "../../api/bills/bills";
import { getBillTypes } from "../../api/bills/billTypes";
import { format } from "date-fns";

const columns = [
  { key: "rollNumber", title: "No" },
  { key: "date", title: "Date" },
  { key: "reason", title: "Reason" },
  { key: "type", title: "Type" },
  { key: "amount", title: "Amount" },
  { key: "edit", title: "Edit" },
  { key: "delete", title: "Delete" },
];

const BillTable = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const userClaims = useUserClaims(user);
  const { openSnackbar } = useSnackbar();
  const { setIsSubmitting } = useContext(SpinnerContext);
  const userData = getRequiredUserData();

  const [data, setData] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [searchedData, setSearchedData] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [billTypes, setBillTypes] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");

  // Load bill types for filter dropdown - only once
  useEffect(() => {
    if (userData.requiredId) {
      const unsubscribe = getBillTypes(userData.requiredId, setBillTypes);
      return () => unsubscribe();
    }
  }, [userData.requiredId]);

  const loadInitialData = async () => {
    try {
      if (userData.requiredId) {
        getBills(userData.requiredId, setData, null, 10, [], typeFilter, userData.active);
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [userData.requiredId, typeFilter]);

  useEffect(() => {
    if (data.length > 0) {
      setLastDoc(data[data.length - 1]);
    }
  }, [data]);

  const loadMoreData = useCallback(async () => {
    try {
      if (lastDoc && userData.requiredId) {
        getBills(userData.requiredId, setData, lastDoc, 5, data, typeFilter, userData.active);
        if (data.length > 0) {
          setLastDoc(data[data.length - 1]);
        }
      }
    } catch (error) {
      console.error("Error loading more data:", error);
    }
  }, [lastDoc, data, userData.requiredId, typeFilter]);

  const handleSearch = async (searchText) => {
    if (searchText.trim() === "") {
      setSearchedData([]);
      loadInitialData();
    } else {
      if (userData.requiredId) {
        searchBills(userData.requiredId, searchText, setSearchedData);
      }
    }
  };

  const handleCancel = () => {
    setSearchedData([]);
    loadInitialData();
  };

  const handleEdit = (row) => {
    setEditRow(row);
    setIsEditDialogOpen(true);
  };

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    setIsSubmitting(true);
    setIsDeleteDialogOpen(false);
    try {
      const res = await deleteBill(user, deleteItemId);
      openSnackbar(`${res.data.message}!`, "success");
    } catch (error) {
      openSnackbar(
        error.response?.data?.message ||
          "An error occurred while deleting the bill.",
        "error"
      );
    }
    setIsSubmitting(false);
    setDeleteItemId(null);
  };

  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
    setSearchedData([]); // Clear search when filter changes
  };

  const handleFormSuccess = () => {
    loadInitialData();
  };

  // Helper function to safely format dates
  const formatDate = (dateValue) => {
    try {
      if (!dateValue) return "N/A";

      let date;

      // Handle Firestore timestamp
      if (dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      }
      // Handle regular Date object
      else if (dateValue instanceof Date) {
        date = dateValue;
      }
      // Handle date string
      else if (typeof dateValue === "string") {
        date = new Date(dateValue);
      }
      // Handle timestamp number
      else if (typeof dateValue === "number") {
        date = new Date(dateValue);
      } else {
        return "N/A";
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "N/A";
      }

      return format(date, "MMM dd, yyyy");
    } catch (error) {
      console.error("Error formatting date:", error, dateValue);
      return "N/A";
    }
  };

  // Process data to add roll numbers and format dates
  const processData = (dataArray) => {
    return dataArray.map((item, index) => ({
      ...item,
      rollNumber: index + 1,
      date: formatDate(item.date),
    }));
  };

  const tableData =
    searchedData.length > 0 ? processData(searchedData) : processData(data);

  // Check if user has permission to edit/delete
  const canEdit = userClaims.admin || userClaims.subAdmin;

  return (
    <Box m="1rem 0">
      {/* Header */}
      <Typography
        variant="h2"
        color={theme.palette.secondary[100]}
        fontWeight="bold"
        sx={{ mb: "20px" }}
      >
        Bills
      </Typography>

      {/* Controls Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Create Button */}
        <Grid item xs={12} sm={3}>
          <BillCreateForm onSuccess={handleFormSuccess} billTypes={billTypes} />
        </Grid>

        {/* Search */}
        <Grid item xs={12} sm={6}>
          <SearchInput onSearch={handleSearch} onCancel={handleCancel} />
        </Grid>

        {/* Type Filter */}
        <Grid item xs={12} sm={3}>
          <TextField
            select
            fullWidth
            label="Filter by Type"
            value={typeFilter}
            onChange={handleTypeFilterChange}
            InputLabelProps={{
              style: { color: theme.palette.secondary[100] },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    backgroundColor: theme.palette.background.alt,
                    color: theme.palette.secondary[200],
                  },
                },
              },
            }}
          >
            <MenuItem value="">All Types</MenuItem>
            {billTypes.map((billType) => (
              <MenuItem key={billType.id} value={billType.type}>
                {billType.type}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <DynamicTable
        data={tableData}
        columns={
          canEdit
            ? columns
            : columns.filter((col) => !["edit", "delete"].includes(col.key))
        }
        loadMoreData={loadMoreData}
        onEdit={canEdit ? handleEdit : undefined}
        onDelete={canEdit ? handleDelete : undefined}
        containerHeight={500}
      />

      {/* Edit Dialog */}
      {isEditDialogOpen && (
        <BillForm
          bill={editRow}
          isOpen={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          onSuccess={handleFormSuccess}
          billTypes={billTypes}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        handleDialogClose={() => setIsDeleteDialogOpen(false)}
        handleConfirmed={handleDeleteConfirmed}
        message="Are you sure you want to delete this bill?"
        title="Delete Confirmation"
      />
    </Box>
  );
};

export default BillTable;
